<template>
  <div>
    <div class="container reservationRibbon__Wrapper" ref="reservationRibbon" v-if="!specialOffers">
      <div class="reservationRibbon" :class="{'reservationRibbon--SearchButton': searchButton}">
        <button class="reservationRibbon__MobileButton" @click="openReservationPanel" :aria-label="$t('search')" />
        <div class="reservationRibbon__Accommodation reservationRibbon__Button">
          <button type="button" @click="openReservationPanel(0, $event)">
            <Icon name="location"/>
            <ClientOnly>
              <template v-if="ACCOMMODATION_TYPES.includes(booking.accommodation?.id)">
                <span>{{ $t(`reservation.${booking.accommodation.id}`) }}</span>
              </template>
              <template v-else>
                <span>{{ (booking?.accommodation?.accommodation?.name_short || booking?.accommodation?.accommodation?.name) || (booking?.accommodation?.name_short || booking?.accommodation?.name) || $t('reservation.allAccommodations') }}</span>
              </template>
            </ClientOnly>
          </button>
        </div>
        <div class="reservationRibbon__Dates reservationRibbon__Button">
          <button type="button" @click="openReservationPanel(1)">
            <Icon name="calendar"/>
            <ClientOnly>
              <span>{{ formatDate(booking.dates[0]) }} - {{ formatDate(booking.dates[1]) }}</span>
            </ClientOnly>
          </button>
        </div>
        <div class="reservationRibbon__Guests reservationRibbon__Button">
          <button type="button" @click="openReservationPanel(2)">
            <Icon name="guests"/>
            <ClientOnly>
              <div class="reservationRibbon__GuestsNumberDesktop">
                <span>{{ booking.adults_count }} {{ $t('reservation.adults') }}</span>
                <span v-if="booking.children">&nbsp;{{ booking.children }} {{ $t('reservation.children') }}</span>
              </div>
              <div class="reservationRibbon__GuestsNumberMobile">
                <span>{{ booking.adults_count + booking.children }} <span>{{ $t('reservationPanel.guestsNumber', booking.adults_count + booking.children) }}</span></span>
              </div>
            </ClientOnly>
          </button>
        </div>
        <ClientOnly>
          <div class="reservationRibbon__Code reservationRibbon__Button" :class="{'reservationRibbon__Code--Active': booking?.promo_code?.length}">
            <button type="button" :aria-label="$t('reservationPanel.promoCode')" @click="openReservationPanel(3)">
              <Icon name="discount"/>
            </button>
          </div>
        </ClientOnly>
        <ClientOnly>
          <div class="reservationRibbon__Search" v-if="searchButton">
            <Button @click="startSearch">{{ $t('search') }}</Button>
          </div>
        </ClientOnly>
      </div>
    </div>
  
    <div v-show="isReservationPanelActive" class="reservationPanel">
      <div :class="{ isActive: isReservationPanelActive }">
        <p v-text="$t('reservation.search')" />
        <div class="reservationPanel__ActiveRibbon">
          <div class="reservationPanel__Accommodation" :class="{ isActive: activeSection === SECTION_TYPES[0] }">
            <div class="reservationPanel__Button">
              <div class="reservationPanel__ButtonLabel">
                <img src="/resources/images/icons/location.svg" width="24" height="24" />
                <p>{{ $t('reservationPanel.where') }}</p>
              </div>
              <input
                type="text"
                ref="searchInput"
                v-model="searchInputValue"
                :placeholder="searchPlaceholder"
                @focus="toggleAccommodationStyle"
                @blur="toggleAccommodationStyle"
              />
            </div>
            <button type="button" v-if="searchInputValue.length > 0" @click="clearSearchInput()">
              <Icon name="clear" />
              <span>Clear</span>
            </button>
          </div>
  
          <div class="reservationPanel__Dates" :class="{ isActive: activeSection === SECTION_TYPES[1] }">
            <button type="button" class="reservationPanel__Button" @click="setActiveSection(1)">
              <ClientOnly>
                <div class="reservationPanel__ButtonLabel">
                  <img src="/resources/images/icons/calendar.svg" width="24" height="24" />
                  <p>{{ $t('reservationPanel.when') }}</p>
                </div>
                {{ formatDatepickerDate(booking.dates) }}
              </ClientOnly>
            </button>
            <button type="button" aria-label="Clear" @click="clearDates" class="reservationPanel__ClearDatesButton">
              <Icon name="clear" />
            </button>
          </div>
  
          <div class="reservationPanel__Guests" :class="{ isActive: activeSection === SECTION_TYPES[2] }">
            <button type="button" class="reservationPanel__Button" @click="setActiveSection(2)">
              <ClientOnly>
                <div class="reservationPanel__ButtonLabel">
                  <img src="/resources/images/icons/guests.svg" width="24" height="24" />
                  <p>{{ $t('reservationPanel.guests') }}</p>
                </div>
                <span>{{ $t('availabilityContact.guests', booking.adults_count + booking.children) }}</span>
              </ClientOnly>
            </button>
          </div>
  
          <div class="reservationPanel__Code" :class="{ isActive: activeSection === SECTION_TYPES[3], 'isDisabled': isPromoCodeDisabled }">
            <div class="reservationPanel__Button">
              <div class="reservationPanel__ButtonLabel">
                <img src="/resources/images/icons/discount.svg" width="24" height="24" />
                <p>{{ $t('reservationPanel.promoCode') }}</p>
              </div>
              <ClientOnly>
                <TextField
                  v-model="booking.promo_code"
                  :placeholder="$t('reservation.codePlaceholder')"
                  @focus="setActiveSection(3)"
                />
              </ClientOnly>
            </div>
            <ClientOnly>
              <button type="button" v-if="booking?.promo_code?.length > 0" @click="clearPromoCode()">
                <Icon name="clear" />
                <span>Clear</span>
              </button>
            </ClientOnly>
          </div>
  
          <div class="reservationPanel__Search">
            <Button @click="startSearch">{{ $t('checkRates') }}</Button>
          </div>
        </div>
  
        <div class="container reservationPanel__SectionsWrapper" ref="mobileAccommodations">
          <section v-if="activeSection === SECTION_TYPES[0]">
            <section class="apiFetchError apiFetchError--Warning" :class="{'specialOffersAccommodations': specialOffersActive}" v-if="specialOffersActive">
              <div class="apiFetchError__Wrapper">
                <div class="apiFetchError__Text">
                  <img src="/resources/images/icons/information.svg" width="24" height="24"/>
                  <i18n-t tag="p" scope="global" keypath="reservationPanel.showingDatesAccommodation">
                    <template #offer>
                      {{ page?.title }}
                    </template>
                  </i18n-t>
                </div>
              </div>
            </section>
            <div class="reservationPanel__AccommodationsDesktop">
              <transition name="fade" mode="out-in">
                <ReservationSearchResults
                  v-if="isSearchResultVisible"
                  :data="searchResults"
                  @set-location="setLocation"
                />
                <div v-else class="reservationPanel__AccommodationsWrapper">
                  <div v-for="category in ACCOMMODATION_TYPES">
                    <div class="reservationPanel__AccommodationTop">
                      <template v-if="category === ACCOMMODATION_TYPES[0]">
                        <Icon name="location" />
                      </template>
                      <template v-else-if="category === ACCOMMODATION_TYPES[1]">
                        <Icon name="hotel" />
                      </template>
                      <template v-else>
                        <Icon name="campsite" />
                      </template>
                      <span v-text="$t(`reservation.${category}`)" />
                    </div>
                    <Button
                      secondary
                      small
                      @click="getAccommodationGroupLink(category)"
                    >
                      {{ $t('reservation.searchAll') }}
                    </Button>
                    <div class="reservationPanel__AccommodationsListing">
                      <span>
                        <template v-if="category === ACCOMMODATION_TYPES[1]">
                          {{ $t('reservation.featuredHotels') }}
                        </template>
                        <template v-else-if="category === ACCOMMODATION_TYPES[2]">
                          {{ $t('reservation.featuredCamps') }}
                        </template>
                      </span>
                      <template v-if="category === ACCOMMODATION_TYPES[0]">
                        <button v-for="destination in destinations.places" @click="setLocation(destination)">
                          <span>{{ destination.name }}</span>
                        </button>
                      </template>
                      <template v-else-if="category === ACCOMMODATION_TYPES[1]">
                        <button v-for="hotel in destinations.hotels" @click="setLocation(hotel.accommodation || {})">
                          <span>{{ hotel.accommodation.name_short || hotel.accommodation.name }}</span>
                          <span v-if="hotel.location?.name" class="reservationPanel__ItemLocation">
                            {{ hotel.location?.name }}
                          </span>
                        </button>
                      </template>
                      <template v-else-if="category === ACCOMMODATION_TYPES[2]">
                        <button v-for="camp in destinations.camps" @click="setLocation(camp.accommodation || {})">
                          <span>{{ camp.accommodation.name_short || camp.accommodation.name }}</span>
                          <span v-if="camp.location?.name" class="reservationPanel__ItemLocation">
                            {{ camp.location?.name }}
                          </span>
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
  
            <ReservationPanelMobileHeader @stepBack="returnFromAccommodations" @close="cancelPanel">
              <div class="reservationPanel__AccommodationInput">
                <input
                  type="text"
                  v-model="searchInputValue"
                  :placeholder="searchPlaceholder"
                  @focus="toggleAccommodationStyle"
                  @blur="toggleAccommodationStyle"
                />
                <img src="/resources/images/icons/location.svg" width="24" height="24" />
                <button type="button" v-if="searchInputValue.length > 0" @click="clearSearchInput()" class="reservationPanel__AccomodationInputClear--Mobile">
                  <Icon name="clear" />
                  <span>Clear</span>
                </button>
              </div>
            </ReservationPanelMobileHeader>
  
            <div class="reservationPanel__AccommodationsMobile">
              <transition name="fade">
                <ReservationSearchResults
                  v-if="isSearchResultVisible"
                  :data="searchResults"
                  @set-location="setLocation"
                />
                <div v-else class="reservationPanel__MobileAccommodationsList">
                  <div v-for="destination in destinations.places">
                    <button
                      type="button"
                      @click="setLocation(destination)"
                      class="reservationPanel__DestinationButton reservationPanel__ResultButton"
                    >
                      <img src="/resources/images/icons/location.svg" width="24" height="24" />
                      {{ destination.name }}
                    </button>
                    <button
                      v-for="hotel in destination.accommodations.filter(item => hotelIdentificators.includes(item.type))"
                      @click="setLocation(hotel)"
                      class="reservationPanel__ResultButton"
                    >
                      <img src="/resources/images/icons/hotel.svg" width="24" height="24" />
                      {{ hotel.name }}
                    </button>
                    <button
                      v-for="camp in destination.accommodations.filter(item => !hotelIdentificators.includes(item.type))"
                      @click="setLocation(camp)"
                      class="reservationPanel__ResultButton"
                    >
                      <img src="/resources/images/icons/campsite.svg" width="24" height="24" />
                      {{ camp.name }}
                    </button>
                  </div>
                </div>
              </transition>
  
              <transition name="fade">
                <div
                  v-if="!searchInputValue.length || searchInputValue === $t('reservation.all.destinations')"
                  class="reservationPanel__BottomButtonWrapper"
                >
                  <Button
                    secondary
                    medium
                    @click="setLocation({ id: 'destinations', name: $t('reservation.all.destinations'), group: true })"
                  >
                    {{ $t('reservation.allAccommodation') }}
                  </Button>
                </div>
              </transition>
            </div>
          </section>
  
          <section
            v-else-if="activeSection === SECTION_TYPES[1]"
            class="reservationPanel__DateSection"
            :class="{ 'withDateTabs': booking.accommodation, 'specialOffersActive': specialOffersActive }"
          >
            <ReservationPanelMobileHeader @stepBack="returnFromAccommodations" @close="cancelPanel" :class="{'reservationPanelMobileHeader--SpecialOffersActive': specialOffersActive}">
              <div v-show="booking.duration_type === 'calendar'" class="reservationPanel__DayNames">
                <span v-for="day in DAYS">{{ $t(`days.${day}`) }}</span>
              </div>
            </ReservationPanelMobileHeader>
  
            <section class="apiFetchError apiFetchError--Warning" v-if="specialOffersActive">
              <div class="apiFetchError__Wrapper">
                <div class="apiFetchError__Text">
                  <img src="/resources/images/icons/information.svg" width="24" height="24"/>
                  <i18n-t tag="p" scope="global" keypath="reservationPanel.showingDatesDate">
                    <template #offer>
                      {{ page?.title }}
                    </template>
                  </i18n-t>
                </div>
                <div class="apiFetchError__Button">
                  <button class="button__Text button__Text--Underline" @click="cancelSpecialOffer">{{ $t('reservationPanel.seeAllDates') }}</button>
                </div>
              </div>
            </section>
  
            <template v-if="booking.duration_type === 'calendar'">
              <VueDatePicker
                :locale="lang"
                class="reservationPanel__Calendar"
                v-model="booking.dates"
                range
                :multi-calendars="{ count: 2 }"
                :clearable="false"
                :auto-apply="true"
                :enable-time-picker="false"
                month-name-format="long"
                :format="formatDatepickerDate"
                timezone="utc"
                :hide-offset-dates="true"
                :min-date="new Date()"
                :max-date="twoYearsFromToday()"
                inline
                :disabled-dates="disableDate"
                @update:model-value="updateDates"
              >
                <template #arrow-left>
                  <Icon name="arrow-left-circle"/>
                </template>
                <template #input-icon>
                  <img src="/resources/images/icons/calendar.svg" width="24" height="24" />
                </template>
                <template #day="{ day, date }">
                  <div class="dp__cell__dayValue">
                    {{ day }}
                    <span v-if="date && Object.entries(pricesPerDay).length && pricesPerDay?.[format(date, 'yyyy-MM-dd')]?.amount" class="reservationPanel__CalendarAmount">
                      {{ formatPrice(pricesPerDay[format(date, 'yyyy-MM-dd')].amount, pricesPerDay[format(date, 'yyyy-MM-dd')].currency, null, true) }}
                    </span>
                  </div>
                </template>
                <template #arrow-right>
                  <Icon name="arrow-right-circle" />
                </template>
              </VueDatePicker>
            </template>
  
            <div v-else class="reservationPanel__Duration">
              <h3 v-text="$t('reservation.stayDuration')" />
              <FieldGroup inline>
                <RadioField
                  v-for="item in STAY_OPTIONS"
                  :label="item.title"
                  :value="item.value"
                  v-model="booking.duration"
                />
              </FieldGroup>
  
              <h3 v-text="$t('reservation.startDate')" />
              <FieldGroup inline class="reservationPanel__MonthsSelector">
                <RadioField
                  v-for="item in VACATION_OPTIONS"
                  class="reservationPanel__MonthRadioField"
                  :label="item.title"
                  :value="item.value"
                  v-model="booking.month"
                >
                  <img src="/resources/images/icons/calendar.svg" width="24" height="24" />
                  <span>{{ item.title }}</span>
                  <span>{{ item.year }}</span>
                </RadioField>
              </FieldGroup>
            </div>
  
            <div class="reservationPanel__MobileConfirmButton">
              <Button @click="setActiveSection(2)">{{ $t('reservation.confirm') }}</Button>
            </div>
          </section>
  
          <section v-else-if="activeSection === SECTION_TYPES[2]">
            <section class="apiFetchError apiFetchError--Warning" v-if="specialOffersActive">
              <div class="apiFetchError__Wrapper">
                <div class="apiFetchError__Text">
                  <img src="/resources/images/icons/information.svg" width="24" height="24"/>
                  <i18n-t tag="p" scope="global" keypath="reservationPanel.showingDatesGuests">
                    <template #offer>
                      {{ page?.title }}
                    </template>
                  </i18n-t>
                </div>
              </div>
            </section>
            <ReservationPanelMobileHeader @stepBack="returnFromAccommodations" @close="cancelPanel" />
            <section class="apiFetchError mobileChange apiFetchError--Warning" v-if="!childFriendly">
              <div class="apiFetchError__Wrapper">
                <div class="apiFetchError__Text">
                  <img src="/resources/images/icons/information.svg" width="24" height="24"/>
                  <p>{{ $t('reservation.notChildFriendly') }}</p>
                </div>
              </div>
            </section>
            <div class="reservationPanel__GuestsFields">
              <CounterField
                :label="$t('reservation.adults')"
                v-model="booking.adults_count"
                :maxValue="page?.max_adults_count || 10"
                :minValue="1"
                inline
              ></CounterField>
              <CounterField
                :label="$t('reservation.children')"
                v-model="booking.children"
                :maxValue="10"
                :minValue="0"
                :disabled="!childFriendly"
                inline
              ></CounterField>
              <TextField
                v-for="(child, index) in booking.children"
                :label="`${$t('reservation.childAge')} ${index + 1}`"
                v-model="booking.children_age[index]"
                :value="booking.children_age[index] || 0"
                :validate="{ type: ['number', 'min', 'max'], min: 0, max: 13, message: { max: $t('formErrors.childrenMaxAge') } }"
                min="0"
                type="number"
                grid
              />

            </div>
            <div class="reservationPanel__MobileConfirmButton">
              <Button @click="returnFromAccommodations">{{ $t('reservation.confirm') }}</Button>
            </div>
          </section>
  
          <section class="reservationPanel__Section promoCode" v-else-if="activeSection === SECTION_TYPES[3]" v-if="specialOffersActive">
            <section class="apiFetchError apiFetchError--Warning">
              <div class="apiFetchError__Wrapper">
                <div class="apiFetchError__Text">
                  <img src="/resources/images/icons/information.svg" width="24" height="24"/>
                  <i18n-t tag="p" scope="global" keypath="reservationPanel.showingDatesPromo">
                    <template #offer>
                      {{ page?.title }}
                    </template>
                  </i18n-t>
                </div>
              </div>
            </section>
          </section>
        </div>
  
        <button type="button" @click="cancelPanel" class="reservationPanel__CloseButton">
          {{ $t('close') }}
          <Icon name="close" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { add, isSameDay, differenceInDays, eachDayOfInterval, isBefore } from 'date-fns'
  import { format, toZonedTime } from 'date-fns-tz';
  import VueDatePicker from '@vuepic/vue-datepicker'
  const { t } = useI18n();
  const route = useRoute()
  const { query } = route;
  const { template, page } = usePage()
  const { lang } = useLanguage();
  const { link, accommodationsData } = useSetup();
  const { initialData: { value: {accommodation_kind_groups: accommodationKindGroups}}, aggregationPagesLinks } = useSetup();
  const { booking, destinations, submitSearchData, updateStorage, restoreFromStorage } = useBooking()
  const { isReservationPanelActive, activeSection, SECTION_TYPES, openPanel, closePanel, setActiveSection } = useReservationPanel();

  const props = defineProps({
    sticky: {
      type: Boolean,
      default: false
    },
    searchButton: {
      type: Boolean,
      default: false,
    },
    specialOffers: {
      type: Boolean,
      default: false,
    },
  })
  const emit = defineEmits(['search'])
  defineExpose({
    openReservationPanel,
  });

  // Refs
  const searchInput = ref(null)
  const mobileAccommodations = ref(null)
  const reservationRibbon = ref(null)
  const accommodationAvailableDates = ref(null);
  const pricesPerDay = ref({});
  const specialOffersAccommodation = ref(null);
  const specialOffersActive = ref(false);

  if(props.specialOffers) {
    specialOffersActive.value = true;
  }

  let scrollTimeout
  let isSpecialOfferSetup = false
  // query from setLocation is applied only on search, so closing the panel doesn't change the url
  let pendingQuery = null

  const resultsVisibleAfterFirstInteraction = ref(true);
  const isMobile = ref(false)
  const searchResults = ref({})
  const searchPlaceholder = ref(t('reservation.findYourStay'))
  const searchInputValue = ref('')
  const hotelIdentificators = accommodationKindGroups.find(item => item.items.includes('HOTEL')).items;
  const isPromoCodeDisabled = ref(false);

  const DURATION_TYPES = ['calendar', 'flexible']
  const ACCOMMODATION_TYPES = ['destinations', 'hotel', 'camp']
  const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
  const childFriendly = computed(() => {
    const accommodationId = booking.value.accommodation?.id;
    if (!accommodationId || ACCOMMODATION_TYPES.includes(accommodationId)) return true;
    return !!accommodationsData.value[accommodationId]?.child_friendly;
  });

  const isSearchResultVisible = computed(() => {
    return booking.value?.accommodation && searchInputValue?.value?.length > 0 && resultsVisibleAfterFirstInteraction.value
  });

  const numberOfResults = computed(() =>
    ['places', 'hotels', 'camps'].reduce((t, i) => (t += destinations.value[i]?.length), 0)
  );

  // used for disable dates in vuedatepicker
  // check if selectedAccommodation has availability_calendar and return all dates from range
  function collectAccommodationAvailableDates() {
    if (booking.value?.accommodation?.id) {
      let selectedAccommodation = accommodationsData.value[booking.value.accommodation.id];
      if (!selectedAccommodation?.availability_calendar) return null;
      try {
        return getAllDatesFromRange(selectedAccommodation.availability_calendar);
      } catch(error) {
        return null;
      }
    }
    return null;
  };

  // vuedatepicker can call method on the disabled-dates prop for every date visible in calendar.
  // For every date we must return true if date is disabled
  function disableDate(date) {
    if (!accommodationAvailableDates.value) return false;
    return !accommodationAvailableDates.value.find(item => isSameDay(item, date));
  }

  // get all dates from accommodation availability_calendar and set them in one array
  function getAllDatesFromRange(range) {
    let dates = [];
    if (!range.length) return [];
    for (let item of range) {
      dates = [...dates, ...eachDayOfInterval({ start: new Date(item.date_from), end: new Date(item.date_to) })]
    }
    return dates;
  }

  async function getAccommodationGroupLink(category) {
    let path = link('AccommodationList');
    if (category === 'hotel') path = aggregationPagesLinks.value.find(item => item.isHotel).sef || '';
    else if (category === 'camp') path = aggregationPagesLinks.value.find(item => !item.isHotel).sef || '';

    if (category) booking.value.accommodation = { id: category, name: t(`reservation.all.${category}`) }
    dataLayerSearch(t(`reservation.all.${category}`));
    delete booking.value.accommodation_id;
    delete booking.value.place_id;
    delete booking.value.promo_code;
    updateStorage();
    await navigateTo(path);
    closePanel();
  }

  function handleResize() {
    isMobile.value = window.innerWidth <= 768
  }

  function handleScroll() {
    clearTimeout(scrollTimeout)
    scrollTimeout = setTimeout(() => {
      if (reservationRibbon.value) {
        reservationRibbon.value.classList[reservationRibbon.value.getBoundingClientRect().top <= 0 ? 'add' : 'remove'](
          'reservationRibbon__Wrapper--Sticky'
        )
      }
    }, 100)
  }

  function changeDurationType(type) {
    booking.value.duration_type = type
  }

  function openReservationPanel(section, e) {
    if ((booking.value?.accommodation?.type || booking.value?.accommodation?.kind) || props.specialOffers) {
      isPromoCodeDisabled.value = false;
      //booking.value.promo_code = '';
    }
    else {
      isPromoCodeDisabled.value = true;
    }
    openPanel(section);
    if (section === 0 && searchInputValue.value) handleSearch(searchInputValue.value)
    if (e && searchInput.value)
      nextTick(() => {
        searchInput.value.value = ''
        searchInput.value.focus()
      })
  }

  function clearPromoCode(section) {
    booking.value.promo_code = '';
    openReservationPanel(section);
  }

  function formatDatepickerDate(date) {
    return `${formatDate(date[0])} - ${formatDate(date[1])}`
  }

  function clearDates() {
    booking.value.dates = [format(new Date(), 'yyyy-MM-dd'), format(add(new Date(), { days: 1 }), 'yyyy-MM-dd')]
    booking.value.date_from = booking.value.dates[0]
    booking.value.date_to = booking.value.dates[1]
  }

  function updateDates(dates) {
    const jsDates = dates.map(date => format(toZonedTime(new Date(date), 'Europe/Zagreb'), 'yyyy-MM-dd', { timeZone: 'Europe/Zagreb' }));
    booking.value.dates = jsDates;
    if (isSameDay(jsDates[0], jsDates[1])) booking.value.dates = [jsDates[0], add(jsDates[1], { days: 1 })]
    if (differenceInDays(jsDates[1], jsDates[0]) > 90) booking.value.dates = [jsDates[0], add(jsDates[0], { days: 90 })]

    booking.value = {
      ...booking.value,
      date_from: booking.value.dates[0],
      date_to: booking.value.dates[1]
    }
  }

  function toggleAccommodationStyle(e) {
    if (e.type === 'focus') {
      if (window?.innerWidth <= 768) searchInput.value.blur();
      setActiveSection(0)
    }
    searchPlaceholder.value = t(`reservation.${e.type === 'focus' ? 'destinationOrHotel' : 'findYourStay'}`)
  }

  async function setLocation(location) {
    let _booking = JSON.parse(JSON.stringify(booking.value))
    let _query = JSON.parse(JSON.stringify(query));
    // for group search we need to remove place_id and accommodation_id and send only id
    if (location.group) {
      delete _booking.place_id;
      delete _booking.accommodation_id;
      delete _query.place_id;
      delete _query.accommodation_id;
    } else {
      if (location.accommodations) {
        delete _booking.accommodation_id
        delete _query.accommodation_id;
        _booking.place_id = location.id
      } else {
        delete _booking.place_id
        _booking.accommodation_id = location.id
        delete _query.place_id;
      }
    }

    booking.value = {
      ..._booking,
      accommodation: JSON.parse(JSON.stringify(location))
    }
    
    if (!booking.value?.accommodation || (booking.value?.accommodation && !booking.value?.accommodation.type)) {
      if(!props.specialOffers) {
        isPromoCodeDisabled.value = true;
        booking.value.promo_code = '';
      }
    }
    else {
      isPromoCodeDisabled.value = false;
    }

    if (!location.group) {
      if (isReservationPanelActive.value) pendingQuery = _query;
      else await useRouter().replace({ query: _query });
    }

    searchInputValue.value = location.accommodation?.name ? (location.accommodation.name_short || location.accommodation.name) : (location.name_short || location.name)
    // To prevent watch booking.value.dates to trigger
    setTimeout(() => {
      setActiveSection(1)
    }, 1)
  }

  // close without search - discard panel changes and reset booking to the last searched state from session storage
  function cancelPanel() {
    restoreFromStorage();
    pendingQuery = null;
    const accommodation = booking.value?.accommodation;
    if (ACCOMMODATION_TYPES.includes(accommodation?.id)) searchInputValue.value = t(`reservation.${accommodation.id}`);
    else if (accommodation?.accommodation) searchInputValue.value = accommodation.accommodation.name_short || accommodation.accommodation.name;
    else searchInputValue.value = accommodation?.name_short || accommodation?.name || '';
    closePanel();
  }

  function returnFromAccommodations() {
    setActiveSection(null)
    if (!booking.value.accommodation) searchInputValue.value = ''
  }

  function handleSearch(value) {
    if (value?.length > 1) {
      searchResults.value = destinations.value.places
        .reduce((acc, nextItem) => {
          if ((nextItem.name_short || nextItem.name).toLowerCase().includes(value.toLowerCase())) acc.push(nextItem)

          if (nextItem.accommodations) {
            for (let item of nextItem.accommodations) {
              if ((item.name_short || item.name).toLowerCase().includes(value.toLowerCase())) {
                acc.push(item)
              }
            }
          }
          return acc
        }, [])
        .map(item => {
          item.displayTitle = (item.name_short || item.name).replace(new RegExp(value, 'gi'), match => `<strong>${match}</strong>`)
          return item
        })
    } else searchResults.value = []
  }

  function clearBookingData() {
    if(!props.specialOffers) {
      booking.value.rate_external_id = '';
      booking.value.package_external_id = '';
      booking.value.selected_units = [];
      booking.value.unit_id = '';
      booking.value.is_loyalty = null;
    }
  }

  function clearSearchInput(){
    isPromoCodeDisabled.value = true;
    if(isPromoCodeDisabled.value) {
      booking.value.promo_code = '';
    }
    searchInputValue.value = '';
    if (activeSection.value !== SECTION_TYPES[0]) activeSection.value = SECTION_TYPES[0]

    if(props.specialOffers) {
      specialOffersActive.value = false;
    }
  }

  async function startSearch() {
    clearBookingData();
    // calendar shows booking.dates, so search must use them even if user didn't change dates
    if (booking.value.dates?.[0] && booking.value.dates?.[1]) {
      booking.value.date_from = format(toZonedTime(new Date(booking.value.dates[0]), 'Europe/Zagreb'), 'yyyy-MM-dd', { timeZone: 'Europe/Zagreb' });
      booking.value.date_to = format(toZonedTime(new Date(booking.value.dates[1]), 'Europe/Zagreb'), 'yyyy-MM-dd', { timeZone: 'Europe/Zagreb' });
    }
    const _childrenAge = booking.value.children_age.filter(item => !['', null].includes(item));
    booking.value.children_age = _childrenAge;
    booking.value.children = _childrenAge.length;
    if (booking.value.children_age.length) {
      booking.value.children_age = booking.value.children_age.map(item => parseInt(item) > 13 ? 13 : item)
    }
    updateStorage();
    if (pendingQuery) {
      await useRouter().replace({ query: pendingQuery });
      pendingQuery = null;
    }
    if (booking.value?.accommodation && ['destinations', 'hotel', 'camp'].includes(booking.value.accommodation.id) || booking.value?.accommodation?.id === '') {
      getAccommodationGroupLink(booking.value.accommodation.id);
      emit('search');
    } else {
      const _template = await submitSearchData();
      if(template.value === _template) emit('search')
      dataLayerSearch();
      closePanel();
    }
  }

  function dataLayerSearch(category) {
    let check_in_date = booking?.value?.date_from;
    let check_out_date = booking?.value?.date_to;
    let no_adults = booking?.value?.adults_count;
    let no_children = booking?.value?.children;
    let coupon = undefined;
    let duration = undefined;
    let no_nights = undefined;
    if(check_in_date && check_out_date) {
      let diffTime = Math.abs(new Date(check_out_date) - new Date(check_in_date));
      duration = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      no_nights = duration;
    }
    if(booking?.value.promo_code) {
      coupon = booking?.value.promo_code;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'view_search_results',
      'search_term': category ? category : searchInputValue.value,
      'check_in_date': check_in_date,
      'check_out_date': check_out_date,
      'no_nights': no_nights,
      'no_adults': no_adults,
      'no_children': no_children,
      'coupon': coupon,
    });
  }

  async function fetchAccommodationPrices() {
    const accommodationId = booking.value.accommodation?.id;
    if (!accommodationId || booking.value?.accommodation?.accommodations?.length || ACCOMMODATION_TYPES.includes(accommodationId)) return;
    try {
      let response = null;
      if(specialOffersActive.value) {
        response = await useClientFetch(`/search/offer_calendar/?offer_id=${page?.value?.id}`, {
          method: 'GET',
        })
      }
      else {
        response = await useClientFetch(`/search/calendar/?accommodation=${accommodationId}`, {
          method: 'GET',
        })
      }

      if (response.dates?.length) {
        // Set prices per day for the selected accommodation
        pricesPerDay.value = response.dates.reduce((acc, nextItem) => {
          acc[nextItem.date] = nextItem.price;
          return acc;
        }, {})

        // Collect available dates for the selected accommodation
        accommodationAvailableDates.value = response.dates.map(item => new Date(item.date));
      } else {
        accommodationAvailableDates.value = null;
      }
    } catch(error) {
      if(props.specialOffers) {
        specialOffersActive.value = false;
      }
      console.error('Error fetching accommodation prices:', error);
    }
  }

  async function cancelSpecialOffer() {
    specialOffersActive.value = false;
    await fetchAccommodationPrices();
  }

  onMounted(async () => {
    if (!props.specialOffers) await fetchAccommodationPrices();
    if (booking.value.accommodation?.accommodation) {
      searchInputValue.value = booking.value.accommodation.accommodation.name_short || booking.value.accommodation.accommodation.name;
    } else if (booking.value?.accommodation) {
      searchInputValue.value = booking.value.accommodation.name_short || booking.value.accommodation.name;
    }

    if(props.specialOffers) {
      isSpecialOfferSetup = true;
      specialOffersAccommodation.value = accommodationsData.value[page.value.accommodations[0]];
      setLocation(specialOffersAccommodation.value);
      // travel_date_from can be in the past, so dates, date_from and date_to must all be moved to today together
      const offerFrom = isBefore(new Date(page?.value?.travel_date_from), new Date()) ? new Date() : new Date(page?.value?.travel_date_from);
      const offerTo = add(offerFrom, { days: page?.value?.nights_count || 1 });
      booking.value.dates = [offerFrom, offerTo].map(date => format(toZonedTime(date, 'Europe/Zagreb'), 'yyyy-MM-dd', { timeZone: 'Europe/Zagreb' }));
      booking.value.date_from = booking.value.dates[0];
      booking.value.date_to = booking.value.dates[1];
      booking.value.adults_count = page?.value?.min_adults_count;
      booking.value.promo_code = page?.value?.promo_code;
      booking.value.rate_external_id = page?.value?.rate_id;
      booking.value.rate_id = page?.value?.rate_id;
      booking.value.package_external_id = page?.value?.package_external_id;
      booking.value.unit_id = page?.value?.unit_id;
      //booking.value.loyalty_prices = true;
      booking.value.loyalty_prices = page?.value?.is_loyalty !== null ? page.value.is_loyalty : true;
      booking.value.is_loyalty = page?.value?.is_loyalty;
      await nextTick();
      isSpecialOfferSetup = false;
      pricesPerDay.value = {};
      await fetchAccommodationPrices();
    }
    else {
      booking.value.rate_id = '';
    }

    setTimeout(() => {
      handleResize()
    }, 1);
    window.addEventListener('resize', handleResize)
    if (props.sticky) window.addEventListener('scroll', handleScroll)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize)
    if (props.sticky) window.removeEventListener('scroll', handleScroll)
  })

  watch(() => route.path, () => {
    if (isReservationPanelActive.value) cancelPanel()
  })

  watch(() => searchInputValue.value, value => {
    handleSearch(value);
    resultsVisibleAfterFirstInteraction.value = true;
  })

  watch(
    () => booking.value.dates,
    () => {
      if (activeSection.value === SECTION_TYPES[1] && booking.value.dates[0] && booking.value.dates[1] && window?.innerWidth > 768) {
        setActiveSection(2)
      }
    }
  )

  watch(() => booking.value.accommodation.id, async (newValue, oldValue) => {
    if (isSpecialOfferSetup) return;
    pricesPerDay.value = {};
    if (ACCOMMODATION_TYPES.includes(booking.value.accommodation.id)) {
      searchInputValue.value = t(`reservation.${booking.value.accommodation.id}`);
    } else {
      searchInputValue.value = booking.value.accommodation.name_short || booking.value.accommodation.name
    }
    if(props.specialOffers) {
      specialOffersActive.value = false;
    }
    await fetchAccommodationPrices();
  })

  watch(childFriendly, value => {
    if (!value) booking.value.children = 0;
  }, { immediate: true })

  watch(() => booking.value.children, () => {
    booking.value.children_age = booking.value.children_age.slice(0, booking.value.children)
  })

  watch(() => isReservationPanelActive.value, value => {
    if (value) resultsVisibleAfterFirstInteraction.value = false
    else resultsVisibleAfterFirstInteraction.value = true
  })
</script>

<style lang="scss">
  @import url('@/assets/styles/components/_reservationPanel.scss');
  @import url('@/assets/styles/components/_datePicker.scss');
</style>
